import React from 'react';
import {AIA_RELOCATION_WORKSPACE_INSET} from '@packages/systems/designer/assistant';
import {emotionStyled, Z_INDEXES} from '@packages/utilities/styled';

// Paints the gutter ring and 8px-radius corners of the inset designer "card"
// over its fixed chrome siblings (top bar, sidebars) while a chat surface is
// docked. Two paint layers: the element's own square box-shadow fills the
// entire 8px gutter (a shadow's outer edge follows the element's radius, so the
// element stays square to keep the ring's outer corners square — a rounded
// outer edge leaves a visible notch against the backdrop), and the ::before
// layer re-paints the ring with an 8px radius to round the inner edge over the
// square chrome corners, which a real border-radius cannot clip since the
// chrome elements are fixed-position siblings rather than children of a
// clippable wrapper. zIndexPopover sits one above zIndexLeftSidebar (the
// highest chrome at a card corner) and below overlays, modals, and toasts.
// (CODE-2595)
const FrameView = emotionStyled('div')({
  bottom: AIA_RELOCATION_WORKSPACE_INSET,
  // The token system does not resolve $-tokens inside compound shadow values;
  // --colors-background-3 is the css var behind $backgroundTertiary.
  boxShadow: `0 0 0 ${AIA_RELOCATION_WORKSPACE_INSET}px var(--colors-background-3)`,
  pointerEvents: 'none',
  position: 'fixed',
  zIndex: Z_INDEXES.zIndexPopover,

  '&::before': {
    borderRadius: '$4',
    boxShadow: `0 0 0 ${AIA_RELOCATION_WORKSPACE_INSET}px var(--colors-background-3)`,
    content: '""',
    inset: 0,
    position: 'absolute',
  },
});

export interface DockedWorkspaceFrameProps {
  /** Edge the chat surface docks to. */
  side: 'left' | 'right';
  /** Width (px) of the docked surface the card sits beside. */
  surfaceWidth: number;
  /** Gap (px) between the docked surface and the card. */
  dockGutter?: number;
  /** Offset (px) from the viewport top, e.g. to sit below a full-width top bar. */
  topOffset?: number;
  ['data-automation-id']?: string;
}

/**
 * The rounded gutter frame around the inset designer "card" beside a docked
 * chat surface. Position is derived entirely from the geometry props, so each
 * dock supplies its own values instead of overriding a shared component.
 */
export const DockedWorkspaceFrame = ({
  side,
  surfaceWidth,
  dockGutter = AIA_RELOCATION_WORKSPACE_INSET,
  topOffset = 0,
  'data-automation-id': dataAutomationId,
}: DockedWorkspaceFrameProps) => (
  <FrameView
    data-automation-id={dataAutomationId}
    style={{
      top: topOffset + AIA_RELOCATION_WORKSPACE_INSET,
      // The dock side reserves the surface width + gutter; the opposite
      // horizontal edge keeps the uniform card inset.
      left:
        side === 'left'
          ? surfaceWidth + dockGutter
          : AIA_RELOCATION_WORKSPACE_INSET,
      right:
        side === 'right'
          ? surfaceWidth + dockGutter
          : AIA_RELOCATION_WORKSPACE_INSET,
    }}
  />
);
